import { useState } from "react";
import { createPortal } from "react-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const PARTY_FIELDS = [["name", "Signatory name"], ["company", "Company"], ["designation", "Designation"], ["email", "Email"], ["phone", "Phone"], ["address", "Address"], ["gstin", "GSTIN"], ["pan", "PAN"]];
const MOU_FIELDS = [["scope", "Scope"], ["commercial_terms", "Commercial terms"], ["payment_terms", "Payment terms"], ["timeline", "Timeline"], ["ip_ownership", "IP ownership"], ["quality_standards", "Quality standards"], ["termination", "Termination"], ["dispute_resolution", "Dispute resolution"]];

export default function LegalEditForm({ doc, onClose, onSaved }) {
  const isNda = doc.doc_type === "nda";
  const [f, setF] = useState({
    purpose: doc.purpose || "", effective_date: doc.effective_date || "", end_date: doc.end_date || "", jurisdiction: doc.jurisdiction || "",
    confidentiality_period_years: doc.confidentiality_period_years ?? 3, nda_type: doc.nda_type || "mutual",
    disclosing_party: { ...(doc.disclosing_party || {}) }, receiving_party: { ...(doc.receiving_party || {}) },
    parties: (doc.parties && doc.parties.length ? doc.parties : [{ name: "" }, { name: "" }]).map((p) => ({ ...p })),
    ...Object.fromEntries(MOU_FIELDS.map(([k]) => [k, doc[k] || ""])),
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF({ ...f, [k]: v });
  const setParty = (slot, k, v) => setF({ ...f, [slot]: { ...f[slot], [k]: v } });
  const setP = (i, k, v) => setF({ ...f, parties: f.parties.map((p, j) => (j === i ? { ...p, [k]: v } : p)) });

  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const patch = { purpose: f.purpose, effective_date: f.effective_date, jurisdiction: f.jurisdiction };
      if (isNda) Object.assign(patch, { nda_type: f.nda_type, confidentiality_period_years: Number(f.confidentiality_period_years), disclosing_party: f.disclosing_party, receiving_party: f.receiving_party });
      else Object.assign(patch, { end_date: f.end_date, parties: f.parties.filter((p) => p.name), ...Object.fromEntries(MOU_FIELDS.map(([k]) => [k, f[k]])) });
      const { data } = await api.patch(`/legal/${doc._id}`, patch);
      toast.success("Document details updated"); onSaved(data); onClose();
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };

  const Party = ({ slot, title }) => (
    <div className="card-soft p-4 space-y-3">
      <div className="label-eyebrow">{title}</div>
      <div className="grid sm:grid-cols-2 gap-3">
        {PARTY_FIELDS.map(([k, l]) => (
          <label key={k} className={`block ${k === "address" ? "sm:col-span-2" : ""}`}><span className="text-[11px] text-muted-foreground">{l}</span>
            <input className="input-underline" value={f[slot]?.[k] || ""} onChange={(e) => setParty(slot, k, e.target.value)} data-testid={`legal-edit-${slot}-${k}`} /></label>
        ))}
      </div>
    </div>
  );

  return createPortal(
    <div className="fixed inset-0 z-[70] modal-backdrop flex items-center justify-center p-4 fade-in" onClick={onClose}>
      <form onSubmit={save} onClick={(e) => e.stopPropagation()} className="modal-surface modal-scale p-7 w-full max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="legal-edit-form">
        <div className="label-eyebrow">Edit {doc.doc_type.toUpperCase()}</div>
        <div className="display-3 mt-1 mb-6">{doc.doc_number}</div>
        <div className="grid sm:grid-cols-3 gap-4">
          <label className="block sm:col-span-3"><span className="label-eyebrow">Purpose</span><input className="input-underline" value={f.purpose} onChange={(e) => set("purpose", e.target.value)} data-testid="legal-edit-purpose" /></label>
          <label className="block"><span className="label-eyebrow">Effective date</span><input type="date" className="input-underline" value={f.effective_date} onChange={(e) => set("effective_date", e.target.value)} data-testid="legal-edit-effective" /></label>
          {isNda ? (
            <>
              <label className="block"><span className="label-eyebrow">NDA type</span><select className="input-soft mt-2" value={f.nda_type} onChange={(e) => set("nda_type", e.target.value)}><option value="mutual">Mutual</option><option value="one_way">One-way</option></select></label>
              <label className="block"><span className="label-eyebrow">Confidentiality (years)</span><input type="number" className="input-underline" value={f.confidentiality_period_years} onChange={(e) => set("confidentiality_period_years", e.target.value)} /></label>
            </>
          ) : <label className="block"><span className="label-eyebrow">End date</span><input type="date" className="input-underline" value={f.end_date} onChange={(e) => set("end_date", e.target.value)} /></label>}
          <label className="block sm:col-span-3"><span className="label-eyebrow">Jurisdiction</span><input className="input-underline" value={f.jurisdiction} onChange={(e) => set("jurisdiction", e.target.value)} data-testid="legal-edit-jurisdiction" /></label>
        </div>
        {isNda ? (
          <div className="grid md:grid-cols-2 gap-4 mt-6">
            <Party slot="disclosing_party" title="Disclosing party" />
            <Party slot="receiving_party" title="Receiving party" />
          </div>
        ) : (
          <div className="mt-6 space-y-4">
            <div className="card-soft p-4">
              <div className="label-eyebrow mb-3">Parties</div>
              {f.parties.map((p, i) => (
                <div key={i} className="grid sm:grid-cols-3 gap-3 mb-3">
                  <input className="input-underline" placeholder="Name" value={p.name || ""} onChange={(e) => setP(i, "name", e.target.value)} data-testid={`legal-edit-party-${i}-name`} />
                  <input className="input-underline" placeholder="Company" value={p.company || ""} onChange={(e) => setP(i, "company", e.target.value)} />
                  <input className="input-underline" placeholder="Address" value={p.address || ""} onChange={(e) => setP(i, "address", e.target.value)} />
                </div>
              ))}
              <button type="button" className="btn-ghost btn-sm" onClick={() => set("parties", [...f.parties, { name: "" }])}>+ Add party</button>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              {MOU_FIELDS.map(([k, l]) => <label key={k} className="block"><span className="label-eyebrow">{l}</span><textarea rows={2} className="input-soft mt-2" value={f[k]} onChange={(e) => set(k, e.target.value)} /></label>)}
            </div>
          </div>
        )}
        {doc.current_pdf_id && <div className="mt-5 text-xs text-[color:hsl(var(--warning))]">A PDF is already attached. After saving, re-generate or re-upload the PDF so the signed copy reflects these details.</div>}
        <div className="flex gap-2 mt-8">
          <button type="button" onClick={onClose} className="btn-ghost flex-1">Cancel</button>
          <button type="submit" disabled={busy} className="btn-primary flex-1" data-testid="legal-edit-save">{busy ? "Saving…" : "Save details"}</button>
        </div>
      </form>
    </div>, document.body);
}
